import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

// Client-side sanitizer mirroring the server-side _sanitize_search_query.
// Never throws; returns null when the input should not be logged.
const CONTROL_CHARS = /[\u0000-\u001F\u007F\u200B-\u200F\uFEFF<>]/g;
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;
const LONG_DIGITS_RE = /\d{7,}/;

export function sanitizeSearchInput(raw: string): { normalized: string; raw: string } | null {
  if (!raw) return null;
  let cleaned = raw.replace(CONTROL_CHARS, "").replace(/\s+/g, " ").trim();
  if (!cleaned) return null;
  if (cleaned.length > 80) cleaned = cleaned.slice(0, 80);
  if (EMAIL_RE.test(cleaned)) return null;
  if (LONG_DIGITS_RE.test(cleaned)) return null;
  return { normalized: cleaned.toLowerCase(), raw: cleaned };
}

const LogSchema = z.object({
  query: z.string().min(1).max(80),
  raw: z.string().min(1).max(80),
  locale: z.enum(["en", "es"]).nullable(),
  result_count: z.number().int().min(0).max(1000),
});

export async function logSearch(input: {
  query: string;
  locale: string | null | undefined;
  resultCount: number;
}): Promise<string | null> {
  try {
    const sanitized = sanitizeSearchInput(input.query);
    if (!sanitized) return null;

    const locale = input.locale === "es" ? "es" : input.locale === "en" ? "en" : null;
    const parsed = LogSchema.safeParse({
      query: sanitized.normalized,
      raw: sanitized.raw,
      locale,
      result_count: Math.max(0, Math.floor(input.resultCount || 0)),
    });
    if (!parsed.success) return null;

    const { data, error } = await supabase.rpc("log_search", {
      _query: parsed.data.query,
      _raw: parsed.data.raw,
      _locale: parsed.data.locale,
      _result_count: parsed.data.result_count,
    });
    if (error) return null;
    return (data as string) ?? null;
  } catch {
    return null;
  }
}

const RESULT_TYPES = ["video", "creator", "category"] as const;
type ResultType = (typeof RESULT_TYPES)[number];

export async function logSearchClick(logId: string | null, type: ResultType, refId: string) {
  if (!logId) return;
  const safeType = RESULT_TYPES.includes(type) ? type : null;
  if (!safeType) return;
  const safeRef = String(refId).replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
  if (!safeRef) return;
  try {
    await supabase.rpc("update_search_click", {
      _id: logId,
      _type: safeType,
      _ref_id: safeRef,
    });
  } catch {
    // swallow — analytics is best-effort
  }
}
